import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { HorizontalTabs, type Tab } from "@/components/HorizontalTabs";
import { ProjectsStats } from "../_components/projects-stats";
import { TabProjectOverview } from "./tab-project-overview";
import { TabProjectTeam } from "./tab-project-team";
import { TabProjectAttachments } from "./tab-project-attachments";
import { TabProjectKPIs } from "./tab-project-kpi";
import { TabProjectOKRs } from "./tab-project-okr";
import {
  PROJECT_TAB_KEYS,
  type projectIdKey,
} from "@/routes/company/routes.projects.constants";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  ENUM_PROJECT_STATUSES,
  type ISingleProject,
} from "@/types/(company)/projects.interface";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import Chip from "@/components/chip";
import { ChevronLeft, FolderDot, Pencil } from "lucide-react";
import {
  useGetProjectDetailsByIdQuery,
  usePatchProjectStatusMutation,
} from "@/services/(company)/api.projects";
import { useNavigate, useParams } from "react-router-dom";
import { PATHS } from "@/routes/paths";
import { createElement } from "react";
import { useConfirm } from "@/context/Confirm";
import toast from "react-hot-toast";
import { usePermissions } from "@/hooks/usePermissions";
import { PLATFORM_SECTION_ENUM } from "@/utils/enums";
import { PermissionButton } from "@/components/ui/permission-button";

const STATUS_CHIP_MAP: Record<ENUM_PROJECT_STATUSES, string> = {
  [ENUM_PROJECT_STATUSES.NOT_STARTED]: "default",
  [ENUM_PROJECT_STATUSES.IN_PROGRESS]: "in_progress",
  [ENUM_PROJECT_STATUSES.COMPLETED]: "success",
  [ENUM_PROJECT_STATUSES.ON_HOLD]: "warning",
};

const projectStatusLabel = (t: TFunction, status: ENUM_PROJECT_STATUSES) =>
  t(`projects:${status}`);

const buildTabsContent = (t: TFunction): Tab[] => [
  {
    label: t("projects:overview"),
    value: PROJECT_TAB_KEYS.Overview,
    content: <TabProjectOverview />,
  },
  {
    label: t("projects:team"),
    value: PROJECT_TAB_KEYS.Team,
    content: <TabProjectTeam />,
  },
  {
    label: t("common:labels.attachments"),
    value: PROJECT_TAB_KEYS.Attachements,
    content: <TabProjectAttachments />,
  },
  {
    label: t("projects:kpis"),
    value: PROJECT_TAB_KEYS.Kpis,
    content: <TabProjectKPIs />,
  },
  {
    label: t("projects:okrs"),
    value: PROJECT_TAB_KEYS.Okrs,
    content: <TabProjectOKRs />,
  },
];

export const ProjectDetailsPage = () => {
  const { t } = useTranslation(["projects", "common"]);
  const { projectId } = useParams<{ [projectIdKey]: string }>();

  const { data: { data: project } = {} } = useGetProjectDetailsByIdQuery(
    { projectId: projectId! },
    { skip: !projectId, refetchOnMountOrArgChange: true },
  );

  return (
    <section className="flex flex-col gap-4">
      <ProjectsStats />
      {project && <ProjectHeader project={project} />}
      <HorizontalTabs tabConfig={buildTabsContent(t)} />
    </section>
  );
};

const ProjectHeader = ({ project }: { project: ISingleProject }) => {
  const { t } = useTranslation(["projects", "common"]);
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [updateProjectStatus, { isLoading: isUpdatingStatus }] =
    usePatchProjectStatusMutation();
  const { can } = usePermissions();

  const handleUpdateStatus = async (status: ENUM_PROJECT_STATUSES) => {
    confirm({
      title: t("projects:update_project_status"),
      msg: t("projects:are_you_sure_you_want_to_change_this_project_s_status"),
      icon: createElement(FolderDot),
      color: "#1AD598",
      onConfirm: async () => {
        await updateProjectStatus({ projectId: project.id.toString(), status })
          .unwrap()
          .then((res) => toast.success(res.message))
          .catch((e) => toast.error(e?.data?.message ?? e?.message));
      },
    });
  };

  return (
    <header className="flex items-center justify-between gap-3 flex-wrap px-3 py-2 rounded-xs border border-gray-200 bg-[#F5F8FF]">
      <div className="flex items-center gap-3 min-w-0">
        <button
          type="button"
          aria-label={t("projects:go_back")}
          onClick={() => navigate(-1)}
          className="h-11 w-11 flex items-center justify-center shrink-0 rounded-xs border border-gray-200 bg-white transition-colors hover:bg-primary/10 hover:border-primary/30"
        >
          <ChevronLeft size={20} className="text-primary rtl:rotate-180" />
        </button>

        <Avatar className="h-10 w-10 rounded-xs shrink-0">
          <AvatarImage src={project.image ?? undefined} />
          <AvatarFallback className="rounded-xs bg-gray-100 text-sm font-bold text-gray-600">
            {project.name.slice(0, 2).toUpperCase()}
          </AvatarFallback>
        </Avatar>

        <div className="flex flex-col leading-tight min-w-0">
          <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest">
            {t("common:shared_ui.project")}
          </span>
          <span className="text-sm font-semibold text-gray-900 truncate">
            {project.name}
          </span>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <Chip
          variant={STATUS_CHIP_MAP[project.status] as any}
          label={projectStatusLabel(t, project.status)}
        />
        <Select
          value={project.status}
          onValueChange={(v) => handleUpdateStatus(v as ENUM_PROJECT_STATUSES)}
          disabled={
            isUpdatingStatus || !can(PLATFORM_SECTION_ENUM.PROJECTS, "edit")
          }
        >
          <SelectTrigger className="h-8 w-fit text-xs border-gray-200 bg-white focus:ring-0">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              {Object.values(ENUM_PROJECT_STATUSES).map((s) => (
                <SelectItem key={s} value={s}>
                  {projectStatusLabel(t, s)}
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
        <PermissionButton
          section={PLATFORM_SECTION_ENUM.PROJECTS}
          action="edit"
          mode="hide"
          variant="outline"
          size="sm"
          className="bg-white border-gray-200 text-gray-700 hover:bg-primary/10 hover:border-primary/30 hover:text-primary"
          onClick={() =>
            navigate(PATHS.COMPANY.COMPANY_PROJECTS_PATHS.EDIT(project.id))
          }
        >
          <Pencil className="w-3.5 h-3.5 me-1.5" />
          {t("projects:edit")}
        </PermissionButton>
      </div>
    </header>
  );
};
